import { useMemo } from 'react';
import type { Mentor, Spread } from '../../api';
import { augmentMentor, type MentorEnhanced } from '../../mocks';

// STA-146 — Mentor card with rating / online status / tags / starting price.
export function MentorCard({
  m,
  delay,
  onPick,
}: {
  m: MentorEnhanced;
  delay: number;
  onPick: () => void;
}) {
  const offline = m.online_status === 'offline';
  // STA-151 design lock — queue_count chip is intentionally hidden in
  // the FE pending the real queue worker. Server still ships the
  // column so the eventual worker has somewhere to write.
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={offline}
      style={{ ['--list-delay' as string]: `${delay}s` }}
      className={`card list-enter mentor-card text-left transition-all ${
        offline ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <div className="flex items-baseline justify-between gap-2">
        <span className="mp-display text-xl sm:text-2xl text-tarot-gold">{m.name}</span>
        <span className="flex items-center gap-1.5 text-xs">
          <span
            className={`inline-block w-2 h-2 rounded-full ${
              m.online_status === 'live'
                ? 'bg-emerald-400 animate-pulse'
                : m.online_status === 'away'
                  ? 'bg-yellow-400'
                  : 'bg-tarot-muted/40'
            }`}
            aria-hidden="true"
          />
          <span className="text-tarot-muted">
            {m.online_status === 'live' ? 'LIVE' : m.online_status === 'away' ? 'AWAY' : 'OFFLINE'}
          </span>
        </span>
      </div>
      <div className="text-xs text-tarot-muted mt-1.5">
        <span className="text-tarot-gold">★ {m.rating.toFixed(1)}</span>{' '}
        <span className="opacity-60">({m.rating_count})</span>
        {m.min_cost_stardust > 0 && (
          <>
            <span className="mx-2">·</span>
            <span className="text-tarot-mauve">起 ★{m.min_cost_stardust}</span>
          </>
        )}
      </div>
      {m.description && (
        <p className="text-tarot-muted text-sm mt-3 line-clamp-2">{m.description}</p>
      )}
      <div className="flex flex-wrap gap-1.5 mt-3">
        {m.tags.map((tag) => (
          <span
            key={tag}
            className="text-[11px] px-2 py-0.5 rounded-full border border-tarot-gold/25 text-tarot-text/80"
          >
            {tag}
          </span>
        ))}
      </div>
    </button>
  );
}

export function MentorFilterChips({
  mentors,
  active,
  onChange,
}: {
  mentors: Mentor[];
  active: string | null;
  onChange: (tag: string | null) => void;
}) {
  const tags = useMemo(() => {
    const set = new Set<string>();
    mentors.forEach((m) => augmentMentor(m).tags.forEach((tg) => set.add(tg)));
    return Array.from(set);
  }, [mentors]);
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-1.5">
      <button
        type="button"
        onClick={() => onChange(null)}
        className={`text-xs px-3 py-1 rounded-full border ${
          active === null
            ? 'border-tarot-gold bg-tarot-gold/15 text-tarot-gold'
            : 'border-tarot-gold/25 text-tarot-muted hover:text-tarot-text'
        }`}
      >
        全部
      </button>
      {tags.map((tag) => (
        <button
          key={tag}
          type="button"
          onClick={() => onChange(tag === active ? null : tag)}
          className={`text-xs px-3 py-1 rounded-full border transition-colors ${
            active === tag
              ? 'border-tarot-gold bg-tarot-gold/15 text-tarot-gold'
              : 'border-tarot-gold/25 text-tarot-muted hover:text-tarot-text'
          }`}
        >
          {tag}
        </button>
      ))}
    </div>
  );
}

// STA-147 — mini layout preview of a spread's positions.
export function SpreadPositions({ spread }: { spread: Spread }) {
  return (
    <div className="mt-4 flex flex-wrap gap-1.5" aria-hidden="true">
      {Array.from({ length: spread.position_count }).map((_, i) => (
        <span
          key={i}
          className="w-6 h-9 rounded-sm border border-tarot-gold/30 bg-tarot-bg"
        />
      ))}
    </div>
  );
}
